import React, { createContext, useContext, ReactNode } from "react";

/** Minimal snapshot shape returned by both v8 compat and v9 modular Firestore SDKs. */
export interface FirestoreDocSnapshot {
    id: string;
    exists: boolean;
    data(): Record<string, any> | undefined;
}

export interface CollectionConstraints {
    where?: [field: string, op: string, value: any][];
    orderBy?: [field: string, direction: "asc" | "desc"];
    limit?: number;
}

/**
 * Thin Firestore abstraction used by ActivityEventSource and its providers.
 *
 * The core app implements this with the v8 compat SDK (global `db`);
 * Rapid implements it with the v9 modular SDK (`getFirestore`).
 */
export interface ActivityFirestore {
    onCollectionSnapshot(
        collectionPath: string,
        constraints: CollectionConstraints,
        onNext: (docs: FirestoreDocSnapshot[]) => void,
        onError: (error: any) => void
    ): () => void;

    onDocSnapshot(
        collectionPath: string,
        docId: string,
        onNext: (doc: FirestoreDocSnapshot) => void,
        onError: (error: any) => void
    ): () => void;
}

interface ActivitiesContextValue {
    workspaceId: string;
    userId: string;
    firestore: ActivityFirestore;
}

const noopFirestore: ActivityFirestore = {
    onCollectionSnapshot: () => () => {},
    onDocSnapshot: () => () => {}
};

const ActivitiesContext = createContext<ActivitiesContextValue>({
    workspaceId: "",
    userId: "",
    firestore: noopFirestore
});

/**
 * Provides workspace/user identity and a Firestore adapter to all descendant
 * useWorkspaceActivities() hooks.
 *
 * Each app wires this once at the root:
 * - Core app: reads user.uid + workspace from globalStore, passes v8 compat db
 * - Rapid: reads from useAuth() + useCurrentWorkspace(), passes v9 modular db
 */
export function ActivitiesProvider({
    workspaceId,
    userId,
    firestore,
    children
}: {
    workspaceId: string;
    userId: string;
    firestore: ActivityFirestore;
    children: ReactNode;
}) {
    return (
        <ActivitiesContext.Provider value={{ workspaceId, userId, firestore }}>
            {children}
        </ActivitiesContext.Provider>
    );
}

export function useActivitiesContext(): ActivitiesContextValue {
    return useContext(ActivitiesContext);
}
